/**
 * FxSystem - consequencia visual do tiro.
 *
 * Regra do modulo: um tiro precisa produzir clarao, luz, tracante, impacto,
 * particula, decalque e cartucho NO MESMO INSTANTE. Se um desses atrasa ou some,
 * o tiro "nao acontece" e a imersao morre. Ao mesmo tempo, exagero em qualquer um
 * deles vira fogos de artificio. Os numeros aqui foram escolhidos para ficarem no
 * limite do discreto: clarao de 2 a 3 frames, tracante em 1 de cada 3 tiros,
 * poeira que dispersa em pouco mais de um segundo.
 *
 * Tudo e pool pre-alocado. Nenhum objeto Three.js nasce depois do init(), e nada
 * dentro de update() aloca. Se um pool enche, o mais antigo e reaproveitado.
 *
 * O que este sistema pede a outros (sempre com encadeamento opcional, porque o
 * dono pode ainda ser um stub):
 *   ctx.lighting.flash(...)   luz dinamica do clarao e da explosao
 *   ctx.cameraRig.addShake()  tremor de explosao
 *   ctx.collision.sampleGround()  altura do chao para o quique de estilhaco
 *   ctx.collision.lineOfSight()   onde o sol chega, para o ar so acender no feixe
 *   ctx.events.emit(SFX)      som de cartucho e de explosao
 *
 * Alem da consequencia do tiro, este sistema mantem o AR: um emissor ambiente
 * permanente (ver AmbientDust em Particles.js), ligado por settings.volumetrics.
 */

import * as THREE from 'three';
import { LAYERS, WORLD } from '../core/Constants.js';
import { Particles, AmbientDust, FX_LAYERS } from './Particles.js';
import { Decals } from './Decals.js';

// --- capacidades dos pools --------------------------------------------------
const TRACER_CAP = 28;
const FLASH_CAP = 10;
const SHELL_CAP = 26;
const DUP_CAP = 12;

// --- ajustes de sensacao ----------------------------------------------------
const FLASH_LIFE = 0.042;        // 2 a 3 frames a 60 fps. Mais que isso vira tocha.
const FLASH_BASE_SCALE = 0.30;   // metros do comprimento da chama
const TRACER_EVERY = 3;          // 1 tracante a cada N tiros
const TRACER_LEN = 2.4;          // comprimento do risco (m)
const TRACER_WIDTH = 0.022;
const SHELL_LIFE = 9.0;

// --- temporarios de modulo --------------------------------------------------
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _q1 = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _e1 = new THREE.Euler();
const _m1 = new THREE.Matrix4();
const _axis = new THREE.Vector3();
const _sc1 = new THREE.Vector3(1, 1, 1);
const _flashColor = new THREE.Color(1.0, 0.80, 0.48);
const _explColor = new THREE.Color(1.0, 0.62, 0.26);

// ---------------------------------------------------------------------------
// Shaders do tracante
// ---------------------------------------------------------------------------

const TRACER_VERT = /* glsl */`
uniform float uTime;
uniform float uLen;
uniform float uWidth;

attribute vec3 tFrom;
attribute vec3 tDir;
attribute vec4 tData;   // birth, life, speed, dist

varying vec2 vUv;
varying float vFade;

void main() {
  float t = uTime - tData.x;
  float life = max(tData.y, 0.001);
  float k = t / life;
  if (t < 0.0 || k >= 1.0) {
    vUv = vec2(0.0);
    vFade = 0.0;
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  float headD = min(tData.z * t, tData.w);
  float tailD = max(headD - uLen, 0.0);
  vec3 dir = normalize(tDir);
  vec3 head = tFrom + dir * headD;
  vec3 tail = tFrom + dir * tailD;
  vec3 wp = mix(tail, head, position.x);

  // largura minima em funcao da distancia: tracante longe nao pode sumir
  vec3 toCam = cameraPosition - wp;
  float d = length(toCam);
  float w = max(uWidth, d * 0.0016);
  vec3 vdir = toCam / max(d, 0.001);
  vec3 side = cross(dir, vdir);
  float sl = length(side);
  if (sl > 0.0001) side /= sl; else side = vec3(0.0, 1.0, 0.0);
  wp += side * (position.y * w);

  gl_Position = projectionMatrix * modelViewMatrix * vec4(wp, 1.0);
  vUv = uv;
  vFade = (1.0 - smoothstep(0.45, 1.0, k)) * smoothstep(0.0, 0.06, k);
}
`;

const TRACER_FRAG = /* glsl */`
uniform vec3 uHead;
uniform vec3 uTail;

varying vec2 vUv;
varying float vFade;

#include <common>

void main() {
  float across = abs(vUv.y - 0.5) * 2.0;
  float core = exp(-across * across * 7.0);
  float along = pow(clamp(vUv.x, 0.0, 1.0), 0.55);
  float a = core * along * vFade;
  if (a < 0.004) discard;
  vec3 col = mix(uTail, uHead, along);
  gl_FragColor = vec4(col, a);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

// ---------------------------------------------------------------------------
// Shaders do clarao de boca
// ---------------------------------------------------------------------------

const FLASH_VERT = /* glsl */`
uniform float uTime;
uniform vec3 uColor;

attribute float aElem;      // 0 = quads cruzados, 1 = estrela, 2 = brilho
attribute vec3 fPos;
attribute vec3 fDir;
attribute vec4 fData;       // birth, life, scale, roll
attribute float fSeed;

varying vec2 vUv;
varying vec4 vCol;

void main() {
  float t = uTime - fData.x;
  float life = max(fData.y, 0.001);
  float k = t / life;
  if (t < 0.0 || k >= 1.0) {
    vUv = vec2(0.0);
    vCol = vec4(0.0);
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  float sc = fData.z * (0.86 + 0.34 * k);
  float roll = fData.w;
  float seed = fSeed;

  vec3 fwd = normalize(fDir);
  vec3 upRef = abs(fwd.y) > 0.95 ? vec3(1.0, 0.0, 0.0) : vec3(0.0, 1.0, 0.0);
  vec3 rt = normalize(cross(upRef, fwd));
  vec3 up = cross(fwd, rt);
  float cr = cos(roll);
  float sr = sin(roll);
  vec3 rr = rt * cr + up * sr;
  vec3 uu = up * cr - rt * sr;

  float tile = 0.0;

  if (aElem < 0.5) {
    // chama: assimetrica por semente, para dois tiros nunca ficarem iguais
    vec3 lp = position;
    lp.x *= 0.62 + 0.62 * fract(seed * 7.31);
    lp.y *= 0.62 + 0.62 * fract(seed * 3.77);
    lp.z *= 0.78 + 0.55 * fract(seed * 5.19);
    vec3 wp = fPos + (rr * lp.x + uu * lp.y + fwd * lp.z) * sc;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(wp, 1.0);
    tile = fract(seed * 11.7) > 0.5 ? 3.0 : 0.0;
  } else {
    vec4 mvPosition = modelViewMatrix * vec4(fPos, 1.0);
    // A boca fica a ~0.6 m do olho: um brilho de meio metro cobriria meia tela.
    // Estrela um pouco maior que a chama, brilho quase do mesmo tamanho.
    float bs = aElem < 1.5 ? 1.20 : 1.00;
    float ang = roll * 1.7 + (aElem < 1.5 ? 0.0 : 0.9);
    float ca = cos(ang);
    float sa = sin(ang);
    vec2 c = position.xy;
    mvPosition.xy += vec2(c.x * ca - c.y * sa, c.x * sa + c.y * ca) * sc * bs;
    gl_Position = projectionMatrix * mvPosition;
    tile = aElem < 1.5 ? 1.0 : 2.0;
  }

  vec2 tOff = vec2(mod(tile, 2.0), floor(tile * 0.5)) * 0.5;
  vUv = tOff + (uv * 0.96 + 0.02) * 0.5;

  float a = pow(1.0 - k, 1.35);
  if (aElem > 1.5) a *= 0.34;
  vCol = vec4(uColor * (1.0 + 0.9 * (1.0 - k)), a);
}
`;

const FLASH_FRAG = /* glsl */`
uniform sampler2D uAtlas;

varying vec2 vUv;
varying vec4 vCol;

#include <common>

void main() {
  vec4 tex = texture2D(uAtlas, vUv);
  float a = tex.a * vCol.a;
  if (a < 0.004) discard;
  gl_FragColor = vec4(vCol.rgb * tex.rgb, a);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

// --- ruido de apoio para as texturas do clarao ------------------------------
function hash2(x, y) {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
  return s - Math.floor(s);
}
function vnoise(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi), b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1), d = hash2(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
function fbm(x, y, oct) {
  let s = 0, amp = 0.5, f = 1;
  for (let i = 0; i < oct; i++) {
    s += vnoise(x * f, y * f) * amp;
    amp *= 0.5;
    f *= 2.03;
  }
  return s;
}

export class FxSystem {
  constructor(ctx) {
    this.ctx = ctx;
    this.particles = null;
    this.decals = null;
    this.ambient = null;

    this._time = 0;
    this._tick = 1;
    this._impactsFrame = 0;
    this._tracerCount = 0;
    this._fireTick = 0;
    this._flashTick = 0;
    this._lastGroundY = 0;
    this._groundSamples = 0;
    this._offs = [];
    this._ready = false;
    this._warned = false;

    // anel para descartar impacto duplicado (evento + chamada direta no mesmo frame)
    this._dup = new Float32Array(DUP_CAP * 4);
    this._dupHead = 0;
  }

  async init() {
    this.particles = new Particles(this.ctx);
    await this.particles.init();

    this.decals = new Decals(this.ctx);
    await this.decals.init();

    // ar com particulado permanente (settings.volumetrics)
    this.ambient = new AmbientDust(this.ctx, this.particles.texture);
    this.ambient.init();

    this.flashTexture = this._buildFlashAtlas();

    this._initTracers();
    this._initFlashes();
    this._initShells();
    this._wireEvents();

    this._ready = true;
  }

  // -------------------------------------------------------------------------
  // API publica
  // -------------------------------------------------------------------------

  /** Impacto de bala numa superficie do cenario. */
  impact(opt) {
    if (!this._ready || !opt) return;
    const p = opt.point;
    if (!p) return;
    const px = p.x, py = p.y, pz = p.z;
    if (!Number.isFinite(px) || !Number.isFinite(py) || !Number.isFinite(pz)) return;
    if (this._isDuplicate(px, py, pz)) return;

    let nx = 0, ny = 1, nz = 0;
    const n = opt.normal;
    if (n && Number.isFinite(n.x)) {
      const l = Math.sqrt(n.x * n.x + n.y * n.y + n.z * n.z);
      if (l > 1e-5) { nx = n.x / l; ny = n.y / l; nz = n.z / l; }
    }

    let surface = opt.surface || opt.object?.userData?.surface || 'concrete';
    if (!this.particles.hasProfile(surface)) surface = 'concrete';

    const budget = this._budget();
    const gy = this._groundY(px, py, pz);

    this.particles.burst(surface, px, py, pz, nx, ny, nz, gy, budget);

    // decalque so nos primeiros impactos do frame: uma rajada de chumbo grosso
    // nao pode consumir a fila inteira num unico frame
    if (budget > 0.25) this.decals.add(px, py, pz, nx, ny, nz, surface, 1);
  }

  /** Nevoa de sangue. Curta e discreta - leitura de acerto, nao gore. */
  blood(opt) {
    if (!this._ready || !opt) return;
    const p = opt.point;
    if (!p || !Number.isFinite(p.x)) return;
    if (this._isDuplicate(p.x, p.y, p.z)) return;

    const cam = this.ctx.camera;
    let ex = 0, ey = 0, ez = 0, edist = 99;
    if (cam) {
      ex = p.x - cam.position.x; ey = p.y - cam.position.y; ez = p.z - cam.position.z;
      edist = Math.sqrt(ex * ex + ey * ey + ez * ez);
      // dano no proprio jogador: a nevoa nasceria colada na lente e pintaria a
      // tela inteira de vermelho. Quem avisa que o jogador levou tiro e o HUD.
      if (edist < 0.9) return;
    }

    let nx = 0, ny = 1, nz = 0;
    const n = opt.normal;
    if (n && Number.isFinite(n.x)) {
      const l = Math.sqrt(n.x * n.x + n.y * n.y + n.z * n.z);
      if (l > 1e-5) { nx = n.x / l; ny = n.y / l; nz = n.z / l; }
    } else if (edist > 1e-4 && edist < 99) {
      // sem normal: joga a nevoa para tras do alvo, saindo da linha do tiro
      nx = ex / edist; ny = ey / edist; nz = ez / edist;
    }

    const gy = this._groundY(p.x, p.y, p.z);
    this.particles.burst('flesh', p.x, p.y, p.z, nx, ny, nz, gy, this._budget());
  }

  /**
   * Clarao de boca. Vive de 2 a 3 frames, gira aleatoriamente e leva junto
   * uma luz dinamica curta e um fiapo de fumaca.
   */
  muzzleFlash(opt) {
    if (!this._ready || !opt) return;
    const p = opt.position;
    if (!p || !Number.isFinite(p.x)) return;

    let dx = 0, dy = 0, dz = -1;
    const d = opt.direction;
    if (d && Number.isFinite(d.x)) {
      const l = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z);
      if (l > 1e-5) { dx = d.x / l; dy = d.y / l; dz = d.z / l; }
    } else if (this.ctx.camera) {
      this.ctx.camera.getWorldDirection(_v1);
      dx = _v1.x; dy = _v1.y; dz = _v1.z;
    }

    const scale = FLASH_BASE_SCALE * (opt.scale || 1) * (0.88 + Math.random() * 0.3);

    // perto da camera = arma em primeira pessoa: vai para a viewScene, que e
    // desenhada depois com o depth limpo e por isso nunca atravessa parede
    const cam = this.ctx.camera;
    let useView = false;
    if (cam) {
      const ex = p.x - cam.position.x, ey = p.y - cam.position.y, ez = p.z - cam.position.z;
      useView = (ex * ex + ey * ey + ez * ez) < 2.25;
    }
    const pool = useView ? this._flashView : this._flashWorld;
    this._writeFlash(pool, p.x, p.y, p.z, dx, dy, dz, scale);

    this._flashTick = this._tick;

    // luz dinamica: e o que faz a parede ao lado acender junto com o tiro
    try {
      this.ctx.lighting?.flash?.(
        _v1.set(p.x + dx * 0.15, p.y + dy * 0.15, p.z + dz * 0.15),
        _flashColor, 9.0 * (opt.scale || 1), 12.0, 0.06
      );
    } catch (err) { this._warnOnce(err); }

    // fumaca residual saindo do cano
    this.particles.emit(
      FX_LAYERS.muzzleSmoke,
      p.x + dx * 0.05, p.y + dy * 0.05, p.z + dz * 0.05,
      dx, dy, dz, p.y - 1.2, 1, (opt.scale || 1)
    );
  }

  /** Risco de tracante. So uma fracao dos tiros leva um. */
  tracer(opt) {
    if (!this._ready || !opt) return;
    const a = opt.from, b = opt.to;
    if (!a || !b || !Number.isFinite(a.x) || !Number.isFinite(b.x)) return;

    this._tracerCount++;
    if (this._tracerCount % TRACER_EVERY !== 0) return;

    let dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist < 1.2) return;
    dx /= dist; dy /= dist; dz /= dist;

    let speed = opt.speed || 780;
    if (speed < 300) speed = 300;
    if (speed > 3000) speed = 3000;

    const pool = this._tracers;
    const idx = pool.head;
    pool.head = (pool.head + 1) % TRACER_CAP;
    const o3 = idx * 3, o4 = idx * 4;
    pool.from[o3] = a.x; pool.from[o3 + 1] = a.y; pool.from[o3 + 2] = a.z;
    pool.dir[o3] = dx; pool.dir[o3 + 1] = dy; pool.dir[o3 + 2] = dz;
    pool.data[o4] = this._time;
    pool.data[o4 + 1] = dist / speed + 0.055;
    pool.data[o4 + 2] = speed;
    pool.data[o4 + 3] = dist;
    pool.attrFrom.needsUpdate = true;
    pool.attrDir.needsUpdate = true;
    pool.attrData.needsUpdate = true;
    pool.geo.instanceCount = TRACER_CAP;
    pool.deathMax = Math.max(pool.deathMax, this._time + pool.data[o4 + 1]);
  }

  /** Cartucho ejetado, com fisica simples, giro e quique no chao. */
  shell(opt) {
    if (!this._ready) return;
    const p = opt?.position;
    if (!p || !Number.isFinite(p.x)) return;

    const S = this._shells;
    const idx = S.head;
    S.head = (S.head + 1) % SHELL_CAP;
    const o3 = idx * 3, o4 = idx * 4;

    S.pos[o3] = p.x; S.pos[o3 + 1] = p.y; S.pos[o3 + 2] = p.z;

    const v = opt.velocity;
    let vx = 1.9, vy = 1.5, vz = 0;
    if (v && Number.isFinite(v.x)) {
      vx = v.x; vy = v.y; vz = v.z;
    } else if (this.ctx.camera) {
      // sem velocidade do chamador: ejeta para a direita e para cima da camera,
      // que e a saida real de uma janela de ejecao (nunca em direcao fixa do mundo)
      const cam = this.ctx.camera;
      cam.getWorldDirection(_v1);
      // direita da camera = cross(frente, cima) com cima = (0,1,0)
      _axis.set(-_v1.z, 0, _v1.x);
      if (_axis.lengthSq() > 1e-6) _axis.normalize(); else _axis.set(1, 0, 0);
      vx = _axis.x * 2.3 + _v1.x * 0.4;
      vy = 1.5;
      vz = _axis.z * 2.3 + _v1.z * 0.4;
    }
    S.vel[o3] = vx + (Math.random() * 2 - 1) * 0.45;
    S.vel[o3 + 1] = vy + (Math.random() * 2 - 1) * 0.35;
    S.vel[o3 + 2] = vz + (Math.random() * 2 - 1) * 0.45;

    S.spin[o3] = (Math.random() * 2 - 1) * 26;
    S.spin[o3 + 1] = (Math.random() * 2 - 1) * 22;
    S.spin[o3 + 2] = (Math.random() * 2 - 1) * 26;

    _q1.setFromEuler(_e1.set(Math.random() * 6.28, Math.random() * 6.28, Math.random() * 6.28));
    S.quat[o4] = _q1.x; S.quat[o4 + 1] = _q1.y; S.quat[o4 + 2] = _q1.z; S.quat[o4 + 3] = _q1.w;

    S.born[idx] = this._time;
    S.ground[idx] = this._groundY(p.x, p.y, p.z);
    S.state[idx] = 1;    // 1 = voando, 2 = repousando
    S.bounces[idx] = 0;

    if (S.live < SHELL_CAP) S.live = Math.max(S.live, idx + 1);
    this._shellMesh.count = S.live;
  }

  /** Explosao: bola de fogo curta, onda de poeira, luz forte e tremor. */
  explosion(opt) {
    if (!this._ready) return;
    const p = opt?.position;
    if (!p || !Number.isFinite(p.x)) return;
    const gy = this._groundY(p.x, p.y, p.z);
    const px = p.x, py = p.y, pz = p.z;

    const P = this.particles;
    P.emit(FX_LAYERS.explFire, px, py, pz, 0, 1, 0, gy, 1, 1);
    P.emit(FX_LAYERS.explSmoke, px, py + 0.2, pz, 0, 1, 0, gy, 1, 1);
    P.emit(FX_LAYERS.explDebris, px, py, pz, 0, 1, 0, gy, 1, 1);
    P.emit(FX_LAYERS.explEmber, px, py, pz, 0, 1, 0, gy, 1, 1);

    // onda de poeira rente ao chao: sai na horizontal, nao em cupula
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.random() * 0.5;
      P.emit(FX_LAYERS.explDust, px, gy + 0.15, pz,
        Math.cos(a), 0.12, Math.sin(a), gy, 0.34, 1);
    }

    try {
      this.ctx.lighting?.flash?.(
        _v1.set(px, py + 0.5, pz), _explColor, 60.0, 26.0, 0.38
      );
    } catch (err) { this._warnOnce(err); }

    const cam = this.ctx.camera;
    if (cam) {
      const dx = cam.position.x - px, dy = cam.position.y - py, dz = cam.position.z - pz;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const inten = Math.max(0, 1.7 * (1 - dist / 26));
      if (inten > 0.02) {
        try { this.ctx.cameraRig?.addShake?.(inten, 0.55); } catch (err) { this._warnOnce(err); }
      }
    }

    this._sfx('explosion', px, py, pz, 1.0, 0.92 + Math.random() * 0.16);
  }

  // -------------------------------------------------------------------------
  // Frame
  // -------------------------------------------------------------------------

  update(dt) {
    if (!this._ready) return;
    if (dt > 0.1) dt = 0.1;

    // O relogio so avanca no FIM do update. Motivo: quase todo efeito nasce em
    // outro sistema (armas, balistica, bots), que roda ANTES do fx no frame.
    // Com o relogio parado ate aqui, o instante de nascimento e exatamente o
    // instante do proximo desenho - o clarao aparece cheio no primeiro frame em
    // vez de ja entrar pela metade da vida.
    const time = this._time;

    // Clarao de reserva: se a arma disparou neste frame e ninguem pediu clarao
    // (WeaponSystem/ViewModel ainda incompletos), o FX cria um na boca estimada.
    if (this._fireTick === this._tick && this._flashTick !== this._tick) {
      this._fallbackFlash();
    }
    this._impactsFrame = 0;
    this._groundSamples = 0;

    this.particles.update(dt, time);
    // depois das particulas: e o Particles quem atualiza a direcao e a cor do
    // sol que o ar ambiente usa para acender
    this.ambient.update(dt, time);
    this.decals.update(dt, time);

    // tracantes e claroes vivem so de uniformes; o shader descarta os mortos
    this._tracers.mat.uniforms.uTime.value = time;
    if (this._tracers.geo.instanceCount !== 0 && time > this._tracers.deathMax) {
      this._tracers.geo.instanceCount = 0;
    }
    this._flashMat.uniforms.uTime.value = time;
    this._tickFlashPool(this._flashWorld, time);
    this._tickFlashPool(this._flashView, time);

    this._updateShells(dt, time);

    this._time = time + dt;
    this._tick++;
  }

  // -------------------------------------------------------------------------
  // Eventos
  // -------------------------------------------------------------------------

  _wireEvents() {
    const E = this.ctx.EVENTS;
    const bus = this.ctx.events;
    if (!bus || !E) return;

    this._offs.push(bus.on(E.BULLET_IMPACT, (d) => {
      if (!d) return;
      const s = d.surface || d.object?.userData?.surface;
      if (s === 'flesh') this.blood(d);
      else this.impact(d);
    }));

    this._offs.push(bus.on(E.ENTITY_DAMAGED, (d) => {
      if (d?.point) this.blood(d);
    }));

    // tiro de bot: a origem do evento e a boca da arma dele
    this._offs.push(bus.on(E.AI_FIRE, (d) => {
      if (!d?.origin) return;
      this.muzzleFlash({ position: d.origin, direction: d.dir, scale: 0.85 });
    }));

    // marca que houve disparo do jogador neste frame (ver update)
    this._offs.push(bus.on(E.WEAPON_FIRE, () => {
      this._fireTick = this._tick;
    }));

    this._offs.push(bus.on(E.PLAYER_LAND, (d) => {
      const imp = d?.impact ?? 0;
      const p = d?.position;
      if (!p || imp < 5) return;
      const mul = Math.min(1.2, (imp - 5) / 8 + 0.35);
      this.particles.emit(FX_LAYERS.landDust, p.x, p.y + 0.04, p.z, 0, 1, 0, p.y, mul, 1);
    }));
  }

  /**
   * Clarao quando o dono da arma nao pediu um. Usa a boca real do ViewModel se
   * ela existir; senao estima a posicao classica de arma em primeira pessoa.
   */
  _fallbackFlash() {
    const cam = this.ctx.camera;
    if (!cam) return;
    let ok = false;
    try {
      const vm = this.ctx.viewModel;
      if (vm && typeof vm.getMuzzleWorldPosition === 'function') {
        // NaN antes de chamar: se o ViewModel for stub e nao escrever nada, o
        // teste abaixo reprova em vez de usar lixo do frame anterior
        _v2.set(NaN, NaN, NaN);
        const r = vm.getMuzzleWorldPosition(_v2);
        if (r && Number.isFinite(r.x)) { _v2.copy(r); ok = true; }
        else if (Number.isFinite(_v2.x)) ok = true;
      }
    } catch (err) { this._warnOnce(err); }

    cam.getWorldDirection(_v1);
    if (!ok) {
      // offset tipico de arma em primeira pessoa: a frente, a direita e um
      // pouco abaixo da linha do olho. Direita = cross(frente, cima).
      _v2.copy(cam.position).addScaledVector(_v1, 0.62);
      _axis.set(-_v1.z, 0, _v1.x);
      if (_axis.lengthSq() > 1e-6) _v2.addScaledVector(_axis.normalize(), 0.14);
      _v2.y -= 0.11;
    }
    this.muzzleFlash({ position: _v2, direction: _v1, scale: 1 });
  }

  // -------------------------------------------------------------------------
  // Tracantes
  // -------------------------------------------------------------------------

  _initTracers() {
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      0, -0.5, 0, 1, -0.5, 0, 1, 0.5, 0, 0, 0.5, 0
    ]), 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([
      0, 0, 1, 0, 1, 1, 0, 1
    ]), 2));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    geo.instanceCount = 0;

    const from = new Float32Array(TRACER_CAP * 3);
    const dir = new Float32Array(TRACER_CAP * 3);
    const data = new Float32Array(TRACER_CAP * 4);
    const attrFrom = new THREE.InstancedBufferAttribute(from, 3);
    const attrDir = new THREE.InstancedBufferAttribute(dir, 3);
    const attrData = new THREE.InstancedBufferAttribute(data, 4);
    attrFrom.setUsage(THREE.DynamicDrawUsage);
    attrDir.setUsage(THREE.DynamicDrawUsage);
    attrData.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('tFrom', attrFrom);
    geo.setAttribute('tDir', attrDir);
    geo.setAttribute('tData', attrData);

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uLen: { value: TRACER_LEN },
        uWidth: { value: TRACER_WIDTH },
        uHead: { value: new THREE.Color(2.3, 1.65, 0.85) },
        uTail: { value: new THREE.Color(0.95, 0.42, 0.10) }
      },
      vertexShader: TRACER_VERT,
      fragmentShader: TRACER_FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.renderOrder = 11;
    mesh.layers.enable(LAYERS.FX);
    mesh.raycast = noopRaycast;
    this.ctx.scene.add(mesh);

    this._tracers = { geo, mat, mesh, from, dir, data, attrFrom, attrDir, attrData, head: 0, deathMax: -1 };
  }

  // -------------------------------------------------------------------------
  // Clarao de boca
  // -------------------------------------------------------------------------

  _initFlashes() {
    this._flashMat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uAtlas: { value: this.flashTexture },
        uColor: { value: new THREE.Color(2.5, 2.15, 1.75) }
      },
      vertexShader: FLASH_VERT,
      fragmentShader: FLASH_FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide
    });

    this._flashWorld = this._makeFlashPool(this.ctx.scene, 13);
    this._flashView = this._makeFlashPool(this.ctx.viewScene, 30);
  }

  _makeFlashPool(scene, renderOrder) {
    const pos = [];
    const uvs = [];
    const elems = [];
    const idx = [];
    const U = [0, 0, 1, 0, 1, 1, 0, 1];
    const quad = (v, e) => {
      const base = pos.length / 3;
      for (let i = 0; i < 4; i++) {
        pos.push(v[i * 3], v[i * 3 + 1], v[i * 3 + 2]);
        uvs.push(U[i * 2], U[i * 2 + 1]);
        elems.push(e);
      }
      idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    };
    const W = 0.34, Z0 = -0.10, Z1 = 1.0;
    quad([-W, 0, Z0, W, 0, Z0, W, 0, Z1, -W, 0, Z1], 0);            // chama plano XZ
    quad([0, -W, Z0, 0, W, Z0, 0, W, Z1, 0, -W, Z1], 0);            // chama plano YZ
    quad([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0], 1); // estrela
    quad([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0], 2); // brilho

    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(uvs), 2));
    geo.setAttribute('aElem', new THREE.BufferAttribute(new Float32Array(elems), 1));
    geo.setIndex(idx);
    geo.instanceCount = 0;

    const fpos = new Float32Array(FLASH_CAP * 3);
    const fdir = new Float32Array(FLASH_CAP * 3);
    const fdata = new Float32Array(FLASH_CAP * 4);
    const fseed = new Float32Array(FLASH_CAP);
    const aPos = new THREE.InstancedBufferAttribute(fpos, 3);
    const aDir = new THREE.InstancedBufferAttribute(fdir, 3);
    const aData = new THREE.InstancedBufferAttribute(fdata, 4);
    const aSeed = new THREE.InstancedBufferAttribute(fseed, 1);
    aPos.setUsage(THREE.DynamicDrawUsage);
    aDir.setUsage(THREE.DynamicDrawUsage);
    aData.setUsage(THREE.DynamicDrawUsage);
    aSeed.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('fPos', aPos);
    geo.setAttribute('fDir', aDir);
    geo.setAttribute('fData', aData);
    geo.setAttribute('fSeed', aSeed);

    const mesh = new THREE.Mesh(geo, this._flashMat);
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.renderOrder = renderOrder;
    mesh.layers.enable(LAYERS.FX);
    mesh.raycast = noopRaycast;
    scene.add(mesh);

    return { geo, mesh, fpos, fdir, fdata, fseed, aPos, aDir, aData, aSeed, head: 0, deathMax: -1 };
  }

  _writeFlash(pool, px, py, pz, dx, dy, dz, scale) {
    const i = pool.head;
    pool.head = (pool.head + 1) % FLASH_CAP;
    const o3 = i * 3, o4 = i * 4;
    pool.fpos[o3] = px; pool.fpos[o3 + 1] = py; pool.fpos[o3 + 2] = pz;
    pool.fdir[o3] = dx; pool.fdir[o3 + 1] = dy; pool.fdir[o3 + 2] = dz;
    pool.fdata[o4] = this._time;
    pool.fdata[o4 + 1] = FLASH_LIFE * (0.9 + Math.random() * 0.25);
    pool.fdata[o4 + 2] = scale;
    pool.fdata[o4 + 3] = Math.random() * Math.PI * 2;
    pool.fseed[i] = Math.random() * 97;
    pool.aPos.needsUpdate = true;
    pool.aDir.needsUpdate = true;
    pool.aData.needsUpdate = true;
    pool.aSeed.needsUpdate = true;
    pool.geo.instanceCount = FLASH_CAP;
    pool.deathMax = Math.max(pool.deathMax, this._time + pool.fdata[o4 + 1]);
  }

  _tickFlashPool(pool, time) {
    if (pool.geo.instanceCount !== 0 && time > pool.deathMax) pool.geo.instanceCount = 0;
  }

  /** Atlas 2x2: 0 e 3 chama, 1 estrela, 2 brilho difuso. */
  _buildFlashAtlas() {
    const S = 256, T = 128;
    const cv = document.createElement('canvas');
    cv.width = S; cv.height = S;
    const c2 = cv.getContext('2d');
    const img = c2.createImageData(S, S);
    const d = img.data;

    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const tx = x >= T ? 1 : 0;
        const ty = y >= T ? 1 : 0;
        const tile = ty * 2 + tx;
        const u = (x - tx * T) / T;
        const v = (y - ty * T) / T;
        const cx = (u - 0.5) * 2;
        const cy = (v - 0.5) * 2;
        const r = Math.sqrt(cx * cx + cy * cy);

        let a = 0, cr = 1, cg = 1, cb = 1;

        if (tile === 0 || tile === 3) {
          // chama: larga na boca, afinando ate a ponta, com linguas irregulares
          const jag = tile === 3 ? 0.62 : 0.44;
          const n = fbm(cx * 2.2 + tile * 9, v * 6.5 + 3, 3);
          let w = 0.92 * Math.pow(Math.max(0, 1 - v), 0.55) * (1 - jag * 0.5 + jag * n);
          w = Math.max(w, 0.02);
          let body = 1 - Math.abs(cx) / w;
          body = body > 0 ? Math.pow(body, 1.35) : 0;
          const tipFade = 1 - Math.pow(v, 2.6);
          a = body * tipFade;
          // nucleo branco quente colado na boca
          const hot = Math.exp(-(cx * cx) * 24 - Math.pow(v * 4.4, 2));
          a = Math.min(1, a + hot * 0.95);
          const mixv = Math.min(1, Math.pow(v, 0.7));
          cr = 1.0;
          cg = 0.95 * (1 - mixv) + 0.5 * mixv;
          cb = 0.86 * (1 - mixv) + 0.16 * mixv;
          cr = cr * (1 - hot) + 1.0 * hot;
          cg = cg * (1 - hot) + 0.99 * hot;
          cb = cb * (1 - hot) + 0.93 * hot;
        } else if (tile === 1) {
          // estrela de 6 pontas com nucleo redondo
          const core = Math.exp(-r * r * 30);
          let sp = 0;
          for (let k = 0; k < 6; k++) {
            const ang = (k / 6) * Math.PI * 2 + 0.3;
            const px2 = cx * Math.cos(ang) + cy * Math.sin(ang);
            const py2 = -cx * Math.sin(ang) + cy * Math.cos(ang);
            if (py2 > 0) {
              const wsp = 0.035 + py2 * 0.10;
              sp = Math.max(sp, Math.exp(-(px2 * px2) / (wsp * wsp)) * Math.max(0, 1 - py2 * 1.25));
            }
          }
          a = Math.min(1, core + sp * 0.85);
          cr = 1.0; cg = 0.93 - 0.18 * (1 - core); cb = 0.78 - 0.35 * (1 - core);
        } else {
          // brilho difuso que assenta o clarao no ambiente
          a = Math.pow(Math.max(0, 1 - r), 2.4) * 0.85;
          cr = 1.0; cg = 0.62; cb = 0.30;
        }

        a = a < 0 ? 0 : (a > 1 ? 1 : a);
        const o = (y * S + x) * 4;
        d[o] = Math.round(Math.min(1, cr) * 255);
        d[o + 1] = Math.round(Math.min(1, cg) * 255);
        d[o + 2] = Math.round(Math.min(1, cb) * 255);
        d[o + 3] = Math.round(a * 255);
      }
    }
    c2.putImageData(img, 0, 0);

    const tex = new THREE.CanvasTexture(cv);
    // ver Particles: o indice de tile e calculado em espaco de pixel do canvas
    tex.flipY = false;
    tex.generateMipmaps = false;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.colorSpace = THREE.NoColorSpace;
    tex.needsUpdate = true;
    return tex;
  }

  // -------------------------------------------------------------------------
  // Cartuchos
  // -------------------------------------------------------------------------

  _initShells() {
    // perfil de um estojo real girado no torno: aro, sulco do extrator, corpo,
    // ombro e boca. Uma peca so, e uma unica draw call para todos os cartuchos.
    const prof = [
      new THREE.Vector2(0.0000, -0.0105),
      new THREE.Vector2(0.0056, -0.0105),
      new THREE.Vector2(0.0056, -0.0092),
      new THREE.Vector2(0.0044, -0.0086),
      new THREE.Vector2(0.0050, -0.0078),
      new THREE.Vector2(0.0050, 0.0040),
      new THREE.Vector2(0.0044, 0.0070),
      new THREE.Vector2(0.0038, 0.0104),
      new THREE.Vector2(0.0032, 0.0100)
    ];
    const geo = new THREE.LatheGeometry(prof, 10);
    geo.computeVertexNormals();

    const mat = new THREE.MeshStandardMaterial({
      color: 0xb08a45,
      metalness: 0.95,
      roughness: 0.32,
      envMapIntensity: 1.1
    });

    const mesh = new THREE.InstancedMesh(geo, mat, SHELL_CAP);
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.layers.enable(LAYERS.FX);
    mesh.raycast = noopRaycast;
    this.ctx.scene.add(mesh);

    this._shellMesh = mesh;
    this._shellGeo = geo;
    this._shellMat = mat;
    this._shells = {
      pos: new Float32Array(SHELL_CAP * 3),
      vel: new Float32Array(SHELL_CAP * 3),
      spin: new Float32Array(SHELL_CAP * 3),
      quat: new Float32Array(SHELL_CAP * 4),
      rest: new Float32Array(SHELL_CAP * 4),
      born: new Float32Array(SHELL_CAP),
      ground: new Float32Array(SHELL_CAP),
      state: new Uint8Array(SHELL_CAP),
      bounces: new Uint8Array(SHELL_CAP),
      head: 0,
      live: 0
    };
  }

  _updateShells(dt, time) {
    const S = this._shells;
    if (S.live === 0) return;
    const g = WORLD.GRAVITY;
    let anyAlive = false;

    for (let i = 0; i < S.live; i++) {
      const st = S.state[i];
      if (st === 0) continue;

      const age = time - S.born[i];
      if (age > SHELL_LIFE + 0.6) {
        S.state[i] = 0;
        _m1.makeScale(0, 0, 0);
        this._shellMesh.setMatrixAt(i, _m1);
        continue;
      }
      anyAlive = true;

      const o3 = i * 3, o4 = i * 4;

      if (st === 1) {
        S.vel[o3 + 1] -= g * dt;
        S.pos[o3] += S.vel[o3] * dt;
        S.pos[o3 + 1] += S.vel[o3 + 1] * dt;
        S.pos[o3 + 2] += S.vel[o3 + 2] * dt;

        const floor = S.ground[i] + 0.006;
        if (S.pos[o3 + 1] <= floor && S.vel[o3 + 1] < 0) {
          const impact = -S.vel[o3 + 1];
          S.pos[o3 + 1] = floor;
          S.vel[o3 + 1] = impact * 0.32;
          S.vel[o3] *= 0.60;
          S.vel[o3 + 2] *= 0.60;
          S.spin[o3] *= 0.45; S.spin[o3 + 1] *= 0.45; S.spin[o3 + 2] *= 0.45;
          S.bounces[i]++;

          if (impact > 0.7) {
            this._sfx('shell', S.pos[o3], S.pos[o3 + 1], S.pos[o3 + 2],
              Math.min(0.6, impact * 0.16), 0.88 + Math.random() * 0.3);
          }
          if (impact < 0.65 || S.bounces[i] > 3) {
            // deitou: guarda a pose de repouso e passa a assentar suavemente
            const yaw = Math.atan2(S.vel[o3 + 2], S.vel[o3]) + (Math.random() - 0.5) * 0.8;
            S.state[i] = 2;
            S.vel[o3] = 0; S.vel[o3 + 1] = 0; S.vel[o3 + 2] = 0;
            _q2.setFromEuler(_e1.set(Math.PI * 0.5, yaw, 0, 'YXZ'));
            S.rest[o4] = _q2.x; S.rest[o4 + 1] = _q2.y; S.rest[o4 + 2] = _q2.z; S.rest[o4 + 3] = _q2.w;
          }
        }

        // integra o giro: quaternion atual multiplicado pelo delta do frame
        _q1.set(S.quat[o4], S.quat[o4 + 1], S.quat[o4 + 2], S.quat[o4 + 3]);
        const sx = S.spin[o3], sy = S.spin[o3 + 1], sz = S.spin[o3 + 2];
        const sl = Math.sqrt(sx * sx + sy * sy + sz * sz);
        if (sl > 1e-4) {
          _axis.set(sx / sl, sy / sl, sz / sl);
          _q2.setFromAxisAngle(_axis, sl * dt);
          _q1.multiply(_q2).normalize();
          S.quat[o4] = _q1.x; S.quat[o4 + 1] = _q1.y; S.quat[o4 + 2] = _q1.z; S.quat[o4 + 3] = _q1.w;
        }
      } else {
        // repouso: assenta o estojo deitado no chao
        _q1.set(S.quat[o4], S.quat[o4 + 1], S.quat[o4 + 2], S.quat[o4 + 3]);
        _q2.set(S.rest[o4], S.rest[o4 + 1], S.rest[o4 + 2], S.rest[o4 + 3]);
        _q1.slerp(_q2, Math.min(1, dt * 9));
        S.quat[o4] = _q1.x; S.quat[o4 + 1] = _q1.y; S.quat[o4 + 2] = _q1.z; S.quat[o4 + 3] = _q1.w;
      }

      // encolhe no fim da vida em vez de sumir de uma vez
      let s = 1;
      if (age > SHELL_LIFE) s = Math.max(0, 1 - (age - SHELL_LIFE) / 0.6);
      _v1.set(S.pos[o3], S.pos[o3 + 1], S.pos[o3 + 2]);
      _q1.set(S.quat[o4], S.quat[o4 + 1], S.quat[o4 + 2], S.quat[o4 + 3]);
      _sc1.set(s, s, s);
      _m1.compose(_v1, _q1, _sc1);
      this._shellMesh.setMatrixAt(i, _m1);
    }

    this._shellMesh.instanceMatrix.needsUpdate = true;
    if (!anyAlive) {
      this._shellMesh.count = 0;
      S.live = 0;
      S.head = 0;
    }
  }

  // -------------------------------------------------------------------------
  // Apoio
  // -------------------------------------------------------------------------

  /** Escala de particulas do frame: uma rajada de 12 bagos nao pode custar 12x. */
  _budget() {
    this._impactsFrame++;
    if (this._impactsFrame <= 4) return 1;
    if (this._impactsFrame <= 8) return 0.55;
    if (this._impactsFrame <= 16) return 0.28;
    return 0.14;
  }

  /**
   * Altura do chao abaixo do impacto, para o quique dos estilhacos. Um raycast
   * por impacto, no maximo 4 por frame - o resto herda o ultimo valor.
   */
  _groundY(x, y, z) {
    if (this._groundSamples >= 4) {
      const gy = this._lastGroundY;
      return (gy < y - 0.005 && gy > y - 10) ? gy : y - 1.0;
    }
    this._groundSamples++;
    let gy = y - 1.0;
    try {
      const s = this.ctx.collision?.sampleGround?.(x, z, y + 0.05);
      if (s && Number.isFinite(s.y)) gy = s.y;
    } catch (err) {
      this._warnOnce(err);
    }
    if (!(gy < y - 0.005)) gy = y - 0.005;
    if (gy < y - 10) gy = y - 10;
    this._lastGroundY = gy;
    return gy;
  }

  /**
   * Descarta impacto repetido no mesmo frame: a balistica pode tanto emitir
   * BULLET_IMPACT quanto chamar fx.impact() direto, e o dobro de poeira no
   * mesmo ponto aparece na tela.
   */
  _isDuplicate(x, y, z) {
    const r = this._dup;
    for (let i = 0; i < DUP_CAP; i++) {
      const o = i * 4;
      if (r[o + 3] !== this._tick) continue;
      const dx = r[o] - x, dy = r[o + 1] - y, dz = r[o + 2] - z;
      if (dx * dx + dy * dy + dz * dz < 1e-7) return true;
    }
    const o = this._dupHead * 4;
    r[o] = x; r[o + 1] = y; r[o + 2] = z; r[o + 3] = this._tick;
    this._dupHead = (this._dupHead + 1) % DUP_CAP;
    return false;
  }

  /** Som pedido ao audio por evento (o FX nao conhece o AudioSystem). */
  _sfx(name, x, y, z, volume, pitch) {
    const bus = this.ctx.events;
    const E = this.ctx.EVENTS;
    if (!bus || !E?.SFX) return;
    bus.emit(E.SFX, {
      name,
      position: new THREE.Vector3(x, y, z),
      volume,
      pitch
    });
  }

  _warnOnce(err) {
    if (this._warned) return;
    this._warned = true;
    console.warn('[fx] chamada a outro sistema falhou, seguindo sem ela:', err);
  }

  // o ar ambiente precisa do tamanho do buffer, mas le direto do renderer por
  // frame (a resolucao tambem muda por settings.resolutionScale, sem resize)
  onResize() { /* nada a fazer aqui */ }

  dispose() {
    for (const off of this._offs) {
      try { off(); } catch (err) { /* ouvinte ja removido */ }
    }
    this._offs.length = 0;

    this.ambient?.dispose();
    this.particles?.dispose();
    this.decals?.dispose();

    if (this._tracers) {
      this._tracers.mesh.parent?.remove(this._tracers.mesh);
      this._tracers.geo.dispose();
      this._tracers.mat.dispose();
    }
    for (const pool of [this._flashWorld, this._flashView]) {
      if (!pool) continue;
      pool.mesh.parent?.remove(pool.mesh);
      pool.geo.dispose();
    }
    this._flashMat?.dispose();
    this.flashTexture?.dispose();

    if (this._shellMesh) {
      this._shellMesh.parent?.remove(this._shellMesh);
      this._shellGeo.dispose();
      this._shellMat.dispose();
    }
    this._ready = false;
  }
}

function noopRaycast() { /* FX nunca entra em raycast de gameplay */ }
